import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { ListView } from '@/shared/components/custom/list-view/components/ListView';
import { ConfirmDialog } from '@/shared/components/custom/confirm-dialog/ConfirmDialog';
import { useConfirm } from '@/shared/hooks/useConfirm';
import { DEFAULT_PAGE_SIZE } from '@/shared/types/pagination';
import { computeVisiblePages } from '@/shared/utils/paginationUtils';
import { useAdminInvitations } from '../hooks/useAdminInvitations';
import { useCancelInvitation } from '../hooks/useCancelInvitation';
import { getInvitationColumns } from '../adapters/invitationColumns';
import type { InvitationDto } from '../types';

export function InvitationsList() {
  const { t } = useTranslation('members');
  const [pageIndex, setPageIndex] = useState(0);
  const [pageSize, setPageSize] = useState(DEFAULT_PAGE_SIZE);
  const [cancellingId, setCancellingId] = useState<string | null>(null);

  const { invitations, totalCount } = useAdminInvitations('', pageIndex, pageSize);
  const cancelInvitation = useCancelInvitation();
  const { confirm, isOpen, options, handleConfirm, handleCancel } = useConfirm();

  const totalPages = totalCount > 0 ? Math.ceil(totalCount / pageSize) : 0;
  const pagination = {
    pageIndex,
    pageSize,
    totalItems: totalCount,
    totalPages,
    canPrevious: pageIndex > 0,
    canNext: totalPages > 0 && pageIndex < totalPages - 1,
    visiblePages: computeVisiblePages(pageIndex, totalPages),
    goToPage: (page: number) => {
      if (totalPages > 0) setPageIndex(Math.max(0, Math.min(page, totalPages - 1)));
    },
    next: () =>
      setPageIndex((prev) => (totalPages > 0 ? Math.min(prev + 1, totalPages - 1) : prev)),
    previous: () => setPageIndex((prev) => Math.max(prev - 1, 0)),
    first: () => setPageIndex(0),
    last: () => {
      if (totalPages > 0) setPageIndex(totalPages - 1);
    },
    changePageSize: (size: number) => {
      setPageSize(size);
      setPageIndex(0);
    },
  };

  async function handleCancelInvitation(inv: InvitationDto) {
    if (!inv.id) return;
    const result = await confirm({
      title: t('invitations.cancelConfirm'),
      description: t('invitations.cancelConfirmDesc', { email: inv.email ?? '' }),
      confirmLabel: t('invitations.cancel'),
      cancelLabel: t('confirm.cancel'),
      variant: 'destructive',
    });
    if (!result.confirmed) return;
    setCancellingId(inv.id);
    try {
      await cancelInvitation.mutateAsync(inv.id);
    } catch {
      // global handler shows error toast
    }
    setCancellingId(null);
  }

  const columns = getInvitationColumns({
    onCancel: handleCancelInvitation,
    isCancelling: (id) => cancellingId === id,
    t,
  });

  return (
    <div>
      <ListView
        data={invitations}
        columns={columns}
        getRowId={(inv) => inv.id ?? inv.email ?? ''}
        pagination={pagination}
        data-testid="invitations-list-table"
      />

      {isOpen && options && (
        <ConfirmDialog
          open={isOpen}
          onOpenChange={(open) => !open && handleCancel()}
          title={options.title}
          description={options.description}
          confirmLabel={options.confirmLabel}
          cancelLabel={options.cancelLabel}
          variant={options.variant}
          onConfirm={handleConfirm}
        />
      )}
    </div>
  );
}
